<template>
    <div class="telekom-angebote-internet">
        <BasicBreadcrumb
            :items="[
                { label: 'Telekom Angebote', to: `/markt/${storeslug}/telekom-angebote` },
                { label: 'Internet', to: '' }
            ]"
        />
        <div class="lg:container mx-auto p-8 lg:px-0 tariffSlider">
            <ContentHeroBanner
                v-for="banner in heroBanner?.items" :key="banner?.id"
                :img-mobile="banner?.imageUrlMobile"
                :img-desktop="banner?.imageUrl"
                :title="banner?.title"
                :background-img-mobile="backgroundImgMobile"
                :background-img-desktop="backgroundImgDesktop"
            />
            <div class="py-2">
                <BasicBtErrorBoundary>
                    <TariffOfferSlider
                        :key="Date.now()"
                        :tenant-id="tenantId"
                        :dsl="true"
                        :title="'Unsere aktuellen TOP-ANGEBOTE der Telekom -<br/>vor Ort im Markt für Sie verfügbar'"
                        :store="currentStore"
                        :link-path="'noLink'"
                    />
                </BasicBtErrorBoundary>
            </div>
            <div class="py-2 internetText">
                <p class="font-bold text-2xl">Internet-Tarife von der Telekom</p>
                <div><strong>Internet-Tarife: MagentaZuhause für Internet und Telefonie im Festnetz</strong><br>
                    <ul class="list-disc ml-6">
                        <li>Schnelles und zuverlässiges Internet</li>
                        <li>Maßgeschneiderte DSL-Angebote für alle</li>
                        <li>Problemloser Wechsel und herausragender Kundenservice</li>
                        <li>Da ein Netz auch fortschrittlich sein kann, wird das Netz der Telekom in Deutschland ab sofort ausschließlich mit Strom aus 100% erneuerbaren Energien betrieben.</li>
                    </ul>
                </div>
            </div>
            <div class="py-8">
                <div class="m-0">
                    <span class="text-xl font-bold mb-6">Häufig gestellte Fragen zu unseren Internet-Tarifen</span>
                </div>
                <div v-for="(faq, index) in faqs" :key="index" class="border-b">
                    <button 
                        class="w-full text-left py-4 flex justify-between items-center focus:outline-none" 
                        @click="toggle(index)"
                    >
                        <span class="font-semibold">{{ faq.question }}</span>
                        <span class="text-xl">
                        {{ activeIndex === index ? "-" : "+" }}
                        </span>
                    </button>
                    <div 
                        v-show="activeIndex === index" 
                        class="pb-4 text-gray-700 transition-all duration-300"
                        v-html="faq.answer"
                    />
                </div>
            </div>
            <div class="py-8 pb-12 container mx-auto font-bold text-paragraph text-2xl text-center">
                Alle Informationen und Angebote zu den Themen Mobilfunk,<br>
                MagentaTV, Glasfaser und Kombipakete findest du hier
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconContract"
                    url="/telekom-angebote/mobilfunk"
                    :title="'Handy mit Vertrag'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconMagenta"
                    url="/telekom-angebote/magentatv"
                    :title="'MagentaTV'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconOptical"
                    url="/telekom-angebote/glasfaser"
                    :title="'Glasfaser'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconCombi"
                    url="/telekom-angebote/kombipakete"
                    :title="'Kombi-Pakete'"
                />
            </div>
        </div>
        <TariffEyeCatcher />
    </div>
</template>

<script setup lang="ts">

    import { hasTenantTariffs } from "~/composables/tariffs";
    import type { ContentBlock } from '~~/models/Tariff/ContentBlock';
    import { ContentBlockType } from '~~/models/Tariff/ContentBlock';

    import backgroundImgMobile from '~/assets/img/tariff-topic/telekom/ssb_BG_mobile.png';
    import backgroundImgDesktop from '~/assets/img/tariff-topic/telekom/ssb_BG.png';
    import backgroundTile from '~/assets/img/tariff-topic/telekom/bg_icons.png';
    import iconContract from '~/assets/img/tariff-topic/telekom/two/mobilfunk.png';
    import iconMagenta from '~/assets/img/tariff-topic/telekom/two/magentaTV.png';
    import iconOptical from '~/assets/img/tariff-topic/telekom/two/glasfaser.png';
    import iconCombi from '~/assets/img/tariff-topic/telekom/two/kombi-pakete.png';

    const tenantId: string = "692ef56ad1b19b630ac62ff3";
    const currentStore = useCurrentStore();
    const hasTariffs = ref<boolean>();

    const route = useRoute();
    const storeslug = route.params.storeslug;    

    const storeName = currentStore?.value?.name ?? 'expert';
    const pageTitle = 'Telekom Internet bei ' + storeName + ' | DSL & Glasfaser Tarife bei expert';
    const pageDescription = 'Telekom Internet bei ' + storeName + ': DSL & Glasfaser Tarife (MagentaZuhause) mit Highspeed und attraktiven Angeboten. Jetzt informieren & Tarif sichern!';
    useHead({
        title: pageTitle,
        meta: [{ name: 'description', content: pageDescription }
        ]
    });      
    
    const { data: heroBanner } = await useFetch<{ items: ContentBlock[] }>(
        `/api/nea/tariff-service/contentBlock`,
        {
            query: {
                page: 0,
                validOnly: true,
                pageSize: 1,
                type: ContentBlockType.TELEKOM_INTERNET_BANNER
            }
        }
    );  

    const activeIndex = ref<number | null>(null);
    function toggle(index: number) {
        activeIndex.value = activeIndex.value === index ? null : index;
    }
    const faqs = [
        {
            question: "Wie funktioniert der Wechsel zu einem Internet-Tarif der Telekom?",
            answer: `Der Wechsel zur Telekom gestaltet sich äußerst unkompliziert. Wir benötigen nur Ihre Adresse und beraten Sie gerne für Ihre optimale Lösung.`
        },{
            question: "Welche Internetgeschwindigkeit ist bei mir möglich?",
            answer: `
                Verwenden Sie unsere Verfügbarkeitsprüfung, indem Sie Ihre Adresse eingeben. Sie erhalten sofort Informationen zu den verfügbaren DSL-Tarifen und Geschwindigkeiten in Ihrer Region. Darüber hinaus informieren wir Sie, sobald Glasfaser-Verbindungen (Fiber) bei Ihnen verfügbar sind.
            `
        },{
            question: "Was ist DSL?",
            answer: `
                Die Abkürzung DSL steht für "Digital Subscriber Line" oder "Digitaler Teilnehmeranschluss". Dieser Übertragungsstandard ermöglicht eine schnelle Datenübertragung. Mit einem DSL-Tarif und einem entsprechenden Anschluss Ihres Internetanbieters können Sie hohe Datenübertragungsraten erreichen. Noch schnellere Verbindungen als DSL bieten nur der VDSL-Standard und Fiber-Verbindungen. Während VDSL auf Glasfaser basiert, reichen Fiber-Verbindungen bis in Ihr Haus oder Ihre Wohnung.
            `
        },{
            question: "Was ist der Unterschied zwischen DSL und VDSL?",
            answer: `
                VDSL steht für "Very High Speed Digital Subscriber Line". Im Vergleich zu DSL, das auf Kupfer basiert, ist VDSL glasfaserbasiert, wobei nur die letzte Verbindungsstrecke auf Kupfer setzt. Besonders auffällig ist die hohe Geschwindigkeit von VDSL, die um ein Vielfaches schneller ist als der DSL-Standard. Dadurch ergeben sich zusätzliche Anwendungsmöglichkeiten wie die parallele Nutzung verschiedener Dienste, das Streaming in hoher Qualität, die Möglichkeit des Homeoffice und vieles mehr.
            `
        },{
            question: "Was bedeutet Megabits pro Sekunde bei DSL?",
            answer: `
                Die Einheit, um die Geschwindigkeit einer Internetverbindung zu messen, ist das Bit pro Sekunde (Bit/s). Dabei gibt sie an, wie viele Bits pro Sekunde übertragen werden. Da die Übertragungsgeschwindigkeiten heute in der Regel sehr hoch sind, werden oft Kilobits (KBit, 1.000 Bits) und Megabits (MBit, 1.000 KBits oder 1.000.000 Bits) verwendet. Zudem ist es wichtig, zwischen Bits und Bytes zu unterscheiden, da viele Datengrößen und Festplattenkapazitäten in Megabyte (MB) oder Gigabyte (GB) angegeben sind. Ein Byte besteht aus 8 Bits. Das bedeutet, um eine Datei mit einer Größe von 20 Megabyte (MB) in einer Sekunde herunterzuladen, benötigen Sie mindestens eine Downloadgeschwindigkeit von 160 MBit/s.
            `
        },{
            question: "Was ist MagentaZuhause?",
            answer: `
                Die Telekom bietet Internet- und Telefon-Tarife an, die eine erstklassige Internetverbindung und hochwertige Telefongespräche in HD-Voice-Qualität bieten.
            `
        },{
            question: "Was brauche ich für MagentaZuhause?",
            answer: `
                Für Ihren neuen Internetanschluss benötigen Sie lediglich einen geeigneten Router. Sie können entweder einen Router direkt bei der Telekom bestellen oder einen bereits vorhandenen Router nutzen. Die Telekom stellt Ihnen eine Liste mit geeigneten Routern für IP-basierte Anschlüsse zur Verfügung, damit Sie die beste Wahl für Ihre Bedürfnisse treffen können.
            `
        },{
            question: "Wie lange dauert die Freischaltung von DSL und VDSL",
            answer: `
                Die Bereitstellung eines VDSL- oder DSL-Anschlusses dauert in der Regel zwischen sechs und vierzehn Werktagen. Sie erhalten den genauen Termin für die Bereitstellung zusammen mit Ihrer Auftragsbestätigung. Falls Sie von einem anderen Internetanbieter zur Telekom wechseln, müssen Sie Ihren bestehenden Tarif nicht selbst kündigen. Die Telekom übernimmt die Kündigung für Sie, um einen unterbrechungsfreien Internetzugang in Ihrem Haushalt sicherzustellen. Wenn Sie zur Telekom wechseln, haben Sie auch die Möglichkeit, Ihre bisherige Festnetznummer zu behalten.
            `
        },{
            question: "Worauf ist bei DSL-Angeboten zu achten?",
            answer: `
                Je nachdem, wie intensiv Sie Ihr Internet zu Hause nutzen und wofür, bietet die Telekom verschiedene MagentaZuhause Tarife, die auf unterschiedliche Nutzerprofile zugeschnitten sind. Um das passende DSL-Angebot für Ihre Anforderungen zu finden, sollten Sie bei der Tarifauswahl folgende Aspekte berücksichtigen:
                <ul class="list-disc ml-6">
                    <li>Preis</li>
                    <li>Geschwindigkeit</li>
                    <li>Vertragslaufzeit</li>
                    <li>Leistungsumfang</li>
                    <li>Eventuelle Zusatzkosten</li>
                </ul>
                Indem Sie diese Faktoren berücksichtigen, können Sie den passenden DSL-Tarif für Ihre individuellen Anforderungen finden.
            `
        },{
            question: "Wie prüfe ich die DSL-Verfügbarkeit für meinen Wohnsitz?",
            answer: `
                Unsere Verfügbarkeitsprüfung ermöglicht es dir auf einfache Weise herauszufinden, welcher DSL-Tarif mit welcher Bandbreite für dich geeignet ist. Du musst lediglich deine Adresse in die entsprechenden Felder eingeben, und wir zeigen dir, welche DSL-Geschwindigkeiten an deinem Standort verfügbar sind.
            `
        },{
            question: "Warum muss ich die DSL-Verfügbarkeit an meinem Wohnort prüfen?",
            answer: `
                Um den richtigen DSL-Tarif für dich zu finden, ist es wichtig zu wissen, welche Down- und Upload-Geschwindigkeiten an deinem Standort verfügbar sind. Basierend auf diesen Informationen kannst du dann den Tarif auswählen, der am besten zu deinen Bedürfnissen passt.
            `
        },{
            question: "Warum sind bestimmte DSL-/VDSL-Tarife an meinem Wohnort nicht verfügbar?",
            answer: `
                Die Verfügbarkeit von DSL-Tarifen hängt stark von den infrastrukturellen Gegebenheiten in Ihrer Wohngegend ab. Dies beinhaltet die Art der Leitungen, die Entfernung zur Vermittlungsstelle und andere Faktoren. Daher kann es von Standort zu Standort unterschiedliche Tarifoptionen geben.
            `
        },{
            question: "Wie funktioniert der DSL-Check bei der Telekom?",
            answer: `
                Mithilfe des DSL-Checks bei der Telekom kannst du in kürzester Zeit herausfinden, welche Internetgeschwindigkeiten bei dir zu Hause verfügbar sind, sowohl im Download als auch im Upload. Wir analysieren die technischen Gegebenheiten an deiner Adresse und zeigen dir die Tarife, die in deinem Fall sinnvoll sind.
            `
        }
    ];

    const checkHasTariffs = async (storeId: string): Promise<void> => {
        console.log(tenantId, storeId)
        hasTariffs.value = await hasTenantTariffs(tenantId, storeId, {mobile: true, subTenant: true});
    };

    await checkHasTariffs(currentStore.value?.id);
    await checkStoreHasPage(tenantId);

    watch(currentStore, async (store) => {
        const storeId: string = store?.id;
        if (!storeId) {
            return;
        }
        await checkHasTariffs(storeId);
        await checkStoreHasPage(tenantId);
    });    
</script>
<style>

    .tariffSlider .vueperslides--fixed-height.vueperslides--bullets-outside {
        margin-bottom: 1em;
    }

    .internetText p {
        margin-bottom: 25px;
    }

</style>